import { DashboardMarket } from "@silo/types";
import { MerklCampaign } from "../merkl-client/types";
import { RawDashboardMarket } from "./types";
import { getDashboardMarketAsset } from "./get-dashboard-market-asset";
import { pipe, Option, Array, Effect } from "effect";
import {
  calculateLtv,
  mapToDebtLtvAsset,
  mapToDepositLtvAsset,
} from "./ltv-helpers";
import { getAssetMerklCampaigns } from "../utils/get-asset-merkl-campaigns";

interface GetDashboardMarketParams {
  rawDashboardMarket: RawDashboardMarket;
  marketMerklCampaigns: MerklCampaign[];
}

export type GetDashboardMarket = ReturnType<typeof getDashboardMarket>;
export type GetDashboardMarketSuccess =
  Effect.Effect.Success<GetDashboardMarket>;
export type GetDashboardMarketError = Effect.Effect.Error<GetDashboardMarket>;
export type GetDashboardMarketContext =
  Effect.Effect.Context<GetDashboardMarket>;

export function getDashboardMarket({
  rawDashboardMarket,
  marketMerklCampaigns,
}: GetDashboardMarketParams) {
  return Effect.gen(function* () {
    const {
      baseAsset: rawBaseAsset,
      bridgeAssets: [rawFirstBridgeAsset, rawSecondBridgeAsset],
      ...rest
    } = rawDashboardMarket;

    const assets = pipe(
      [rawBaseAsset, rawFirstBridgeAsset, rawSecondBridgeAsset],
      Array.flatMapNullable((asset) => asset)
    );

    const depositAssets = pipe(
      assets,
      Array.filter(
        (asset) => asset.collateralBalance > 0n || asset.protectedBalance > 0n
      ),
      Array.map(mapToDepositLtvAsset)
    );

    const debtAssets = pipe(
      assets,
      Array.filter((asset) => asset.debtBalance > 0n),
      Array.map(mapToDebtLtvAsset)
    );

    const ltv = calculateLtv({ depositAssets, debtAssets });

    const baseAsset = yield* getDashboardMarketAsset({
      ltv,
      rawDashboardMarketAsset: rawBaseAsset,
      assetMerklCampaigns: getAssetMerklCampaigns(
        marketMerklCampaigns,
        rawBaseAsset
      ),
    });

    const firstBridgeAsset = yield* getDashboardMarketAsset({
      ltv,
      rawDashboardMarketAsset: rawFirstBridgeAsset,
      assetMerklCampaigns: getAssetMerklCampaigns(
        marketMerklCampaigns,
        rawFirstBridgeAsset
      ),
    });

    const secondBridgeAsset = yield* pipe(
      rawSecondBridgeAsset,
      Option.fromNullable,
      Option.match({
        onNone: () => Effect.succeed(undefined),
        onSome: (rawDashboardMarketAsset) =>
          getDashboardMarketAsset({
            ltv,
            rawDashboardMarketAsset,
            assetMerklCampaigns: getAssetMerklCampaigns(
              marketMerklCampaigns,
              rawDashboardMarketAsset
            ),
          }),
      })
    );

    const dashboardMarket: DashboardMarket = {
      ...rest,

      baseAsset,
      bridgeAssets: secondBridgeAsset
        ? [firstBridgeAsset, secondBridgeAsset]
        : [firstBridgeAsset],
    };

    return dashboardMarket;
  });
}

